import React, { useState } from 'react';
import { Star, Filter, Package, ArrowRight } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const Products = () => {
    const { products, currentUser } = useApp();
    const [filter, setFilter] = useState('All');

    const categories = ['All', ...new Set(products.map(p => p.category))];
    const filteredProducts = filter === 'All'
        ? products
        : products.filter(p => p.category === filter);

    const handleBuyNow = (product) => {
        if (currentUser) {
            alert(`To buy ${product.name}, please go to Member Panel > Shop Products for direct purchase.`);
        } else {
            alert('Please login to purchase products.');
        }
    };

    return (
        <div className="min-h-screen bg-[#f8fafc] font-outfit pt-20 pb-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 md:mb-12 gap-6">
                    <div>
                        <span className="text-indigo-600 font-bold text-xs uppercase tracking-widest mb-2 block">Our Collection</span>
                        <h1 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight">Premium Products</h1>
                        <p className="text-gray-500 mt-3 text-lg font-medium max-w-xl">World-class cleaning solutions designed for effectiveness and safety.</p>
                    </div>
                </div>

                {/* Filter - Horizontal Scroll on Mobile */}
                <div className="sticky top-16 md:top-20 z-30 bg-[#f8fafc]/95 backdrop-blur-sm -mx-4 px-4 py-4 md:mx-0 md:px-0 md:bg-transparent md:backdrop-blur-none mb-8 border-b md:border-none border-gray-200">
                    <div className="flex items-center gap-3 overflow-x-auto pb-2 md:pb-0 scrollbar-hide no-scrollbar mask-linear-fade">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 shadow-sm shrink-0">
                            <Filter className="w-5 h-5 text-gray-400" />
                        </div>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setFilter(cat)}
                                className={`px-6 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-300 border ${filter === cat
                                    ? 'bg-gray-900 text-white border-gray-900 shadow-lg transform scale-105'
                                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Product Grid */}
                {filteredProducts.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
                        {filteredProducts.map((product, index) => (
                            <div key={product.id} className="group flex flex-col bg-white rounded-[2rem] shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 overflow-hidden hover:-translate-y-2">
                                <div className="relative aspect-[3/4] overflow-hidden bg-white">
                                    <div className="absolute inset-0 bg-gray-50 animate-pulse"></div>
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="absolute inset-0 w-full h-full object-contain p-2 transform group-hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                        onLoad={(e) => e.target.previousSibling.style.display = 'none'}
                                    />
                                    <div className="absolute top-4 left-4">
                                        <span className="px-3 py-1.5 rounded-lg bg-white/90 backdrop-blur-md text-[10px] font-black uppercase tracking-wider text-gray-900 shadow-sm border border-white/50">
                                            {product.category}
                                        </span>
                                    </div>
                                    {/* Overlay Action */}
                                    <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                        <button
                                            onClick={() => handleBuyNow(product)}
                                            className="w-full bg-white/95 backdrop-blur-md text-gray-900 font-bold py-3 rounded-xl shadow-lg border border-white/50 flex items-center justify-center gap-2 hover:bg-gray-900 hover:text-white transition-colors"
                                        >
                                            <ArrowRight className="w-4 h-4" />
                                            Buy Now
                                        </button>
                                    </div>
                                </div>

                                <div className="p-6 flex flex-col flex-grow">
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="text-lg font-black text-gray-900 line-clamp-1 group-hover:text-indigo-600 transition-colors" title={product.name}>
                                            {product.name}
                                        </h3>
                                    </div>

                                    <div className="flex items-center mb-4">
                                        <div className="flex text-yellow-400">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className={`w-3.5 h-3.5 ${i < 4 ? 'fill-current' : 'text-gray-200'}`} />
                                            ))}
                                        </div>
                                        <span className="text-xs font-bold text-gray-400 ml-2">4.8 (120+)</span>
                                    </div>

                                    <p className="text-gray-500 text-sm leading-relaxed mb-6 line-clamp-2 flex-grow font-medium">
                                        {product.description}
                                    </p>

                                    <div className="pt-4 mt-auto border-t border-gray-50 flex items-center justify-between">
                                        <div className="flex flex-col">
                                            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Price</span>
                                            <span className="text-xl font-black text-gray-900">₹{product.price}</span>
                                        </div>
                                        <button
                                            onClick={() => handleBuyNow(product)}
                                            className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-900 group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300"
                                        >
                                            <ArrowRight className="w-5 h-5 -rotate-45 group-hover:rotate-0 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Package className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">No products found</h3>
                        <p className="text-gray-500 mt-2">Try selecting a different category.</p>
                        <button
                            onClick={() => setFilter('All')}
                            className="mt-6 text-indigo-600 font-bold hover:underline"
                        >
                            View All Products
                        </button>
                    </div>
                )}
            </div>
            <style dangerouslySetInnerHTML={{
                __html: `
                .no-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .no-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}} />
        </div>
    );
};

export default Products;
